import { Navigate } from 'react-router'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import { PermissionGuard } from '@/app/routes/PermissionGuard.tsx'
import { ProtectedRoute } from '@/app/routes/ProtectedRoute.tsx'
import { PublicRoute } from '@/app/routes/PublicRoute.tsx'
import {
	LazyAddressesPage,
	LazyApartmentsPage,
	LazyAudioPage,
	LazyAuthzAdminPage,
	LazyBrainChatPage,
	LazyCameraClientEditPage,
	LazyCameraCreatePage,
	LazyCameraEditPage,
	LazyCamerasPage,
	LazyChipProgrammingAccessInfoPage,
	LazyChipProgrammingPage,
	LazyChipProgrammingUserDataPage,
	LazyClientEditPage,
	LazyClientsPage,
	LazyCodesPage,
	LazyDatetimePage,
	LazyDdnsPage,
	LazyDiagnosticsPage,
	LazyDoorsPage,
	LazyEqualizerPage,
	LazyFirmwarePage,
	LazyForbiddenPage,
	LazyGatesEditPage,
	LazyGatesMassConfigPage,
	LazyGatesPage,
	LazyGeneralPage,
	LazyGreetingsPage,
	LazyGuidePage,
	LazyHandsetDisconnectPage,
	LazyHomePage,
	LazyKeysPage,
	LazyLoginPage,
	LazyMarqueePage,
	LazyMatrixPage,
	LazyNetworkPage,
	LazyOverviewPage,
	LazyRebootPage,
	LazyReportEventsPage,
	LazyReportsPage,
	LazyRequestDetailsPage,
	LazyRequestsPage,
	LazyResetPage,
	LazySettingsPage,
	LazySipPage,
	LazySyslogPage,
	LazyUsersPage,
	LazyVideoPage
} from '@/app/routes/lazyPages.ts'

import { DASHBOARD_URL, DIAGNOSTICS_CONSTANTS, REQUESTS_CONSTANTS } from '@/config/url.config.ts'
import { diagnosticsPagePermissions } from '@/features/intercoms/diagnostics/diagnosticsPermissions.ts'
import {
	AUTH_PERMISSIONS_LIST,
	DOMOPHONE_PERMISSIONS,
	GREETINGS_PAGE_PERMISSIONS
} from '@/features/permissions/permissions.env.ts'

const diagnosticsRoutes = (
	<>
		<Route index element={<LazyDiagnosticsPage />} />
		<Route
			path={DIAGNOSTICS_CONSTANTS.overview()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.overview()]}>
					<LazyOverviewPage />
				</PermissionGuard>
			}
		/>
		<Route
			path={DIAGNOSTICS_CONSTANTS.general()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.general()]}>
					<LazyGeneralPage />
				</PermissionGuard>
			}
		/>
		<Route path={DIAGNOSTICS_CONSTANTS.keys()} element={<LazyKeysPage />} />
		<Route
			path={DIAGNOSTICS_CONSTANTS.codes()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.codes()]}>
					<LazyCodesPage />
				</PermissionGuard>
			}
		/>
		<Route path={DIAGNOSTICS_CONSTANTS.apartments()} element={<LazyApartmentsPage />} />
		<Route path={DIAGNOSTICS_CONSTANTS.matrix()} element={<LazyMatrixPage />} />
		<Route
			path={DIAGNOSTICS_CONSTANTS.doors()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.doors()]}>
					<LazyDoorsPage />
				</PermissionGuard>
			}
		/>
		<Route
			path={DIAGNOSTICS_CONSTANTS.marquee()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.marquee()]}>
					<LazyMarqueePage />
				</PermissionGuard>
			}
		/>
		<Route path={DIAGNOSTICS_CONSTANTS.audio()} element={<LazyAudioPage />} />
		<Route path={DIAGNOSTICS_CONSTANTS.equalizer()} element={<LazyEqualizerPage />} />
		<Route
			path={DIAGNOSTICS_CONSTANTS.sip()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.sip()]}>
					<LazySipPage />
				</PermissionGuard>
			}
		/>
		<Route
			path={DIAGNOSTICS_CONSTANTS.video()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.video()]}>
					<LazyVideoPage />
				</PermissionGuard>
			}
		/>
		<Route path={DIAGNOSTICS_CONSTANTS.ddns()} element={<LazyDdnsPage />} />
		<Route
			path={DIAGNOSTICS_CONSTANTS.network()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.network()]}>
					<LazyNetworkPage />
				</PermissionGuard>
			}
		/>
		<Route
			path={DIAGNOSTICS_CONSTANTS.syslog()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.syslog()]}>
					<LazySyslogPage />
				</PermissionGuard>
			}
		/>
		<Route
			path={DIAGNOSTICS_CONSTANTS.firmware()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.firmware()]}>
					<LazyFirmwarePage />
				</PermissionGuard>
			}
		/>
		<Route
			path={DIAGNOSTICS_CONSTANTS.datetime()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.datetime()]}>
					<LazyDatetimePage />
				</PermissionGuard>
			}
		/>
		<Route path={DIAGNOSTICS_CONSTANTS.reboot()} element={<LazyRebootPage />} />
		<Route
			path={DIAGNOSTICS_CONSTANTS.reset()}
			element={
				<PermissionGuard permissions={diagnosticsPagePermissions[DIAGNOSTICS_CONSTANTS.reset()]}>
					<LazyResetPage />
				</PermissionGuard>
			}
		/>
	</>
)

export const AppRouter = () => (
	<BrowserRouter>
		<Routes>
			<Route element={<PublicRoute />}>
				<Route path='/login' element={<LazyLoginPage />} />
			</Route>
			<Route path='*' element={<Navigate to='/login' replace />} />

			<Route path='/403' element={<LazyForbiddenPage />} />

			<Route element={<ProtectedRoute />}>
				<Route path='/' element={<LazyHomePage />} />
				<Route
					path={DASHBOARD_URL.home()}
					element={
						<PermissionGuard permissions={[DOMOPHONE_PERMISSIONS.GET_PLANNER_TASKS]}>
							<LazyRequestsPage />
						</PermissionGuard>
					}
				/>
				<Route path={DASHBOARD_URL.requests()} element={<LazyRequestsPage />} />
				<Route path={DASHBOARD_URL.requests()}>
					<Route path={REQUESTS_CONSTANTS.details()} element={<LazyRequestDetailsPage />} />
					<Route path='chip-programming' element={<LazyChipProgrammingPage />} />
					<Route path='chip-programming/user-data' element={<LazyChipProgrammingUserDataPage />} />
					<Route path='chip-programming/access-info' element={<LazyChipProgrammingAccessInfoPage />} />
				</Route>
				<Route path={DASHBOARD_URL.settings()} element={<LazySettingsPage />} />
				<Route path={DASHBOARD_URL.guide()} element={<LazyGuidePage />} />
				<Route path={DASHBOARD_URL.reports()} element={<LazyReportsPage />} />
				<Route path={DASHBOARD_URL.reportEvents()} element={<LazyReportEventsPage />} />
				<Route
					path={DASHBOARD_URL.greetings()}
					element={
						<PermissionGuard
							permissions={GREETINGS_PAGE_PERMISSIONS}
							match='some'
						>
							<LazyGreetingsPage />
						</PermissionGuard>
					}
				/>
				<Route path={DASHBOARD_URL.handsets()} element={<LazyHandsetDisconnectPage />} />

				<Route
					path={DASHBOARD_URL.addresses()}
					element={
						<PermissionGuard
							permissions={[
								DOMOPHONE_PERMISSIONS.GET_ELTC_ADDRESS_TOWNS,
								DOMOPHONE_PERMISSIONS.GET_ELTC_ADDRESS_TOWN_STREETS,
								DOMOPHONE_PERMISSIONS.GET_ELTC_ADDRESS_TOWN_STREET_HOUSES,
								DOMOPHONE_PERMISSIONS.GET_ELTC_ADDRESS_HOUSE_FLATS
							]}
						>
							<LazyAddressesPage />
						</PermissionGuard>
					}
				/>
				<Route path={DASHBOARD_URL.clients()} element={<LazyClientsPage />} />
				<Route path={DASHBOARD_URL.cameras()} element={<LazyCamerasPage />} />
				<Route path={`${DASHBOARD_URL.cameras()}/create`} element={<LazyCameraCreatePage />} />
				<Route path={`${DASHBOARD_URL.cameras()}/edit/:id`} element={<LazyCameraEditPage />} />
				<Route path={`${DASHBOARD_URL.cameras()}/edit/:id/client/:idClient`} element={<LazyCameraClientEditPage />} />
				<Route path='/edit/client/:idAccount' element={<LazyClientEditPage />} />
				<Route path={DASHBOARD_URL.gatesDiagnostics()}>{diagnosticsRoutes}</Route>
				<Route path={DASHBOARD_URL.gatesMassConfig()} element={<LazyGatesMassConfigPage />} />
				<Route
					path={DASHBOARD_URL.gatesEdit()}
					element={
						<PermissionGuard permissions={[DOMOPHONE_PERMISSIONS.GET_GATE_BY_ID]}>
							<LazyGatesEditPage />
						</PermissionGuard>
					}
				/>
				<Route path={DASHBOARD_URL.gates()} element={<LazyGatesPage />} />
				<Route path={DASHBOARD_URL.users()} element={<LazyUsersPage />} />
				<Route
					path={DASHBOARD_URL.brainChat()}
					element={
						<PermissionGuard permissions={[DOMOPHONE_PERMISSIONS.BRAIN_CHAT_ASK]}>
							<LazyBrainChatPage />
						</PermissionGuard>
					}
				/>
				<Route
					path={DASHBOARD_URL.authzAdmin()}
					element={
						<PermissionGuard permissions={AUTH_PERMISSIONS_LIST} match='some'>
							<LazyAuthzAdminPage />
						</PermissionGuard>
					}
				/>
			</Route>
		</Routes>
	</BrowserRouter>
)
